// redux/slices/easycardPaymentsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { resetStore } from "./resetStore";
import { apiRequest } from "../../Utils/apiClient";
import { clearJwtToken } from "../../Utils/authStorage";

const getError = (error, fallback) =>
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback;

const handleAuthError = async (error) => {
    const status = error?.response?.status;
    if (status === 401 || status === 403) {
        try { await clearJwtToken(); } catch { }
    }
};

const getUid = (getState, providedUid) => {
    const state = getState();
    const uid =
        providedUid ||
        state?.master?.master?.uid ||
        state?.master?.uid ||
        state?.auth?.uid ||
        null;
    if (!uid) throw new Error("UID required");
    return uid;
};

export const createEasycardPayment = createAsyncThunk(
    "easycardPayments/create",
    async (paymentData = {}, { getState, rejectWithValue }) => {
        try {
            const uid = getUid(getState, paymentData.uid);
            const res = await apiRequest("POST", "/payments/easycard/create", {
                uid,
                orderReference: paymentData.orderReference,
                amount: paymentData.amount,
                description: paymentData.description,
                redirectUrl: paymentData.redirectUrl,
                products: paymentData.products,
                consumer: paymentData.consumer,
                issueInvoice: paymentData.issueInvoice !== false,
                language: paymentData.language,
            });
            return res?.data || res;
        } catch (error) {
            await handleAuthError(error);
            return rejectWithValue(getError(error, "Create EasyCard payment failed"));
        }
    }
);

const initialState = { creating: false, error: null, lastPayment: null };

const easycardPaymentsSlice = createSlice({
    name: "easycardPayments",
    initialState,
    reducers: {
        clearEasycardPaymentsError: (state) => { state.error = null; },
    },
    extraReducers: (builder) => {
        builder
            .addCase(createEasycardPayment.pending, (state) => { state.creating = true; state.error = null; })
            .addCase(createEasycardPayment.fulfilled, (state, { payload }) => {
                state.creating = false;
                state.lastPayment = payload || null;
            })
            .addCase(createEasycardPayment.rejected, (state, action) => {
                state.creating = false;
                state.error = action.payload || action.error?.message || "Create payment failed";
            })
            .addCase(resetStore, () => initialState);
    },
});

export const { clearEasycardPaymentsError } = easycardPaymentsSlice.actions;
export const selectEasycardPayments = (state) => state.easycardPayments || initialState;
export default easycardPaymentsSlice.reducer;